import { useEffect, useMemo, useState } from 'react'

// ──────────────────────────────────────────────────────────────────────────
// WAITLIST REFERRAL — front-end system of record (temporary).
//
// HOW THE FLOW WORKS END-TO-END
//   1. GENERATE  When someone joins the waitlist, they get a unique referral
//      code → a shareable link `https://<host>/r/<code>`.
//   2. SHARE     They copy / share that link (Web Share API or clipboard).
//   3. LAND      A friend opens `/r/<code>`. The <ReferralCapture> route stores
//      the inbound code (captureInboundReferral) and forwards them to
//      `/signup?waitlist=1&ref=<code>`, which opens the waitlist modal with an
//      "<code> invited you" banner.
//   4. CONVERT   The friend joins. We attribute their signup to the inbound
//      code and the referrer moves up the queue.
//   5. MANAGE    All durable state (positions, attribution, who referred whom)
//      MUST live server-side. Until that backend exists, this module keeps a
//      best-effort record in localStorage so the UI is real and demoable.
//
// ⚠️ BACKEND TODO (replace the localStorage shims below with real API calls):
//   - POST /waitlist            { email, referredBy } -> { code, position }
//   - GET  /waitlist/:code      -> { position, referralCount }
//   The queue math + dedupe + emails are a server responsibility.
// ──────────────────────────────────────────────────────────────────────────

const INBOUND_KEY = 'giga.waitlist.referredBy'
const SELF_KEY = 'giga.waitlist.referralCode'

// A short, URL-safe, human-shareable code. Deterministic length, no ambiguous
// chars. The real code should come from the backend on signup.
function generateCode(): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789'
  let out = ''
  for (let i = 0; i < 7; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)]
  }
  return out
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* storage may be unavailable (private mode) — fail silently */
  }
}

// Persist an inbound referral code (called from the /r/:code capture route).
// Kept so attribution survives the navigation into the signup flow.
export function captureInboundReferral(code: string) {
  const clean = code.trim().toLowerCase()
  if (clean) safeSet(INBOUND_KEY, clean)
}

// Read the inbound referral code: prefer an explicit ?ref= on the current URL,
// else fall back to whatever the capture route stored.
export function getInboundReferral(): string | null {
  try {
    const param = new URLSearchParams(window.location.search)
      .get('ref')
      ?.trim()
      .toLowerCase()
    if (param) {
      safeSet(INBOUND_KEY, param)
      return param
    }
  } catch {
    /* ignore */
  }
  return safeGet(INBOUND_KEY)
}

// The current visitor's OWN referral code — created once and reused so the link
// they copy is stable across the session. Swap for the backend-issued code.
export function getOrCreateSelfCode(): string {
  const existing = safeGet(SELF_KEY)
  if (existing) return existing
  const code = generateCode()
  safeSet(SELF_KEY, code)
  return code
}

// Build the absolute, shareable invite URL for a code.
export function buildReferralLink(code: string): string {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://giga.ai'
  return `${origin}/r/${code}`
}

type ReferralState = {
  // The visitor's own shareable invite link.
  referralLink: string
  // The raw code embedded in that link.
  selfCode: string
  // Who referred THIS visitor, if anyone (drives the "X invited you" banner).
  referredBy: string | null
}

// Hook: resolves the visitor's referral state once on mount. Stable for the
// lifetime of the component so copied links / banners don't flicker.
export function useReferral(): ReferralState {
  const [referredBy, setReferredBy] = useState<string | null>(null)
  const selfCode = useMemo(() => getOrCreateSelfCode(), [])
  const referralLink = useMemo(() => buildReferralLink(selfCode), [selfCode])

  useEffect(() => {
    setReferredBy(getInboundReferral())
  }, [])

  return {
    referralLink,
    selfCode,
    referredBy,
  }
}
